import { Document, Page, Text, View, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import { toWords } from 'number-to-words';
import { checkEmpty, number_format } from '../../../src/helpers/utility';
import { useEffect, useState } from 'react';

// --------------------
// Font
// --------------------
Font.register({
  family: 'BookmanOldStyle',
  fonts: [
    { src: '/bookmanoldstyle.ttf', fontWeight: 'normal', fontStyle: 'normal' },
    { src: '/bookmanoldstyle_italic.ttf', fontWeight: 'normal', fontStyle: 'italic' },
    { src: '/bookmanoldstyle_bold.ttf', fontWeight: 'bold', fontStyle: 'normal' },
    { src: '/bookmanoldstyle_bolditalic.ttf', fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

// --------------------
// Layout constants (pt)
// A4 ~ 595 x 842
// 1cm ~ 28pt
// --------------------
const TOP_GAP = 95; // top whitespace before header
const SIDE_GAP = 28; // ~1cm
const HEADER_HEIGHT = 65; // reserved space for header block
const BOTTOM_GAP = 60; // keep some bottom breathing room (no fixed footer now)

// How much space (pt-ish) we want to reserve for:
// Grand Total + Amount in words + Remarks + Signatures.
// If not enough space left → move whole block to next page.
const CLOSING_BLOCK_MIN_SPACE = 240;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'BookmanOldStyle',
    fontSize: 8,
    paddingTop: TOP_GAP + HEADER_HEIGHT,
    paddingBottom: BOTTOM_GAP,
    paddingLeft: SIDE_GAP,
    paddingRight: SIDE_GAP,
    display: 'flex',
    flexDirection: 'column',
  },

  // --------------------
  // Fixed Header
  // --------------------
  header: {
    position: 'absolute',
    top: TOP_GAP,
    left: SIDE_GAP,
    right: SIDE_GAP,
    height: HEADER_HEIGHT,
    justifyContent: 'flex-end',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerText: { fontSize: 10, fontWeight: 'bold' },
  headerTextInvoiceNo: { fontSize: 12, fontWeight: 'bold' },
  invoiceTitle: { fontSize: 12, fontWeight: 'bold', textTransform: 'uppercase' },
  labelBold: { fontWeight: 'bold' },

  // --------------------
  // Content blocks
  // --------------------
  section: { fontSize: 9, marginBottom: 8 },
  row: { flexDirection: 'row', justifyContent: 'space-between' },
  cell: { flex: 1, marginRight: 6 },
  customerNameLine: {
    marginTop: 2,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    paddingBottom: 3,
    fontWeight: 'bold',
  },

  // --------------------
  // Table
  // --------------------
  tableWrapper: {
    borderWidth: 1,
    borderColor: '#000000',
    marginTop: 8,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    backgroundColor: '#FFFFFF',
  },
  thFirst: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 9,
    borderRightWidth: 1,
    fontWeight: 'bold',
    borderRightColor: '#000000',
  },
  th: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 9,
    borderRightWidth: 1,
    borderRightColor: '#000000',
    fontWeight: 'bold',
  },
  thLast: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 9,
    fontWeight: 'bold',
  },
  tdRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    fontSize: 9,
  },
  td: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    borderRightWidth: 1,
    borderRightColor: '#000000',
    fontSize: 9,
  },
  tdLast: {
    paddingVertical: 3,
    paddingHorizontal: 3,
    fontSize: 9,
  },
  textCenter: { textAlign: 'center' },
  textRight: { textAlign: 'right' },

  // --------------------
  // Grand total
  // --------------------
  grandTotalWrapper: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000000',
    paddingVertical: 4,
    paddingHorizontal: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    marginTop: 6,
    fontSize: 9,
  },
  grandTotalTitle: { fontSize: 9, fontWeight: 'bold' },
  grandTotalText: { fontSize: 10, fontWeight: 'bold' },
  amountInWords: { fontStyle: 'italic', marginTop: 2, fontSize: 9 },

  // --------------------
  // Remarks + Signatures (Normal flow)
  // --------------------
  remarksSignatureWrapper: {
    marginTop: 16, // small gap after grand total
    fontSize: 9,
  },
  remarksLine: {
    marginBottom: 18,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  signaturesContainer: {
    flexDirection: 'row',
    justifyContent: "flex-end",
    margin: '0 auto 0 5rem',
    alignItems: 'flex-end',
  },
  signatureBlock: { width: 153, alignItems: 'center' },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    width: 120,
    height: 14,
    marginBottom: 4,
  },
  signatureLabel: { fontSize: 9, fontWeight: 'bold' },
});

const convertToWords = (number) => {
  if (number === undefined || number === null || number === '') return '';

  let [integerPart, decimalPart] = String(number).split('.');
  let wordsString = 'QAR ';

  if (!checkEmpty(integerPart)) {
    wordsString += toWords(parseInt(integerPart, 10));
  }

  if (!checkEmpty(integerPart) && decimalPart && decimalPart !== '00') {
    wordsString += ' and ';
  }

  if (decimalPart && decimalPart !== '00') {
    wordsString += toWords(parseInt(decimalPart, 10)) + ' dirhams';
  }

  wordsString = wordsString
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  return wordsString + ' Only.';
};

const safeParseJson = (value, fallback) => {
  if (typeof value !== 'string') return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
};

const normalizeList = (value) => {
  const parsed = safeParseJson(value, value);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object') return Object.values(parsed);
  return [];
};

const toNum = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const isDeletedJobRow = (job) => String(job?.type || '').trim().toLowerCase() === 'delete';

const sumChargeRows = (rows = []) =>
  normalizeList(rows).reduce((sum, item) => {
    const qty = toNum(item?.charge_Qty ?? item?.basis_par);
    if (qty <= 0) return sum;
    const unit = toNum(item?.charge_Unit_Amount ?? item?.unit_price);
    return sum + unit * qty;
  }, 0);

const getFirstNumericValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const trimmed = String(value).trim();
    if (!trimmed || ['null', 'undefined', 'nan'].includes(trimmed.toLowerCase())) continue;

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue;
  }

  return 0;
};

const getNormalizedInvoiceJobs = (singlePrintData) => {
  const jobs = normalizeList(singlePrintData?.invoice_line_items ?? singlePrintData?.invoice_job_map);
  return jobs.filter((job) => !isDeletedJobRow(job));
};

const getInvoiceDisplayMode = (singlePrintData, normalizedJobs = []) => {
  const explicitMode = String(singlePrintData?.invoice_Mode || '').trim();
  if (explicitMode) return explicitMode;

  const runtimeJobs = normalizeList(normalizedJobs).filter((job) => !isDeletedJobRow(job));

  const modeNames = [...new Set(runtimeJobs.map((job) => String(job?.job_type_Name || '').trim()).filter(Boolean))];
  if (modeNames.length) return modeNames.join(', ');

  return '';
};

const getChargeRowsFromInvoice = (singlePrintData, normalizedJobs = []) => {
  const activeJobs = normalizeList(normalizedJobs).filter((job) => !isDeletedJobRow(job));
  const chargeRowsFromJobs = activeJobs.flatMap((job) => {
    const candidates = [job?.jobCharge, job?.job_data?.jobCharge, job?.job_data?.job_charge];
    for (const candidate of candidates) {
      const rows = normalizeList(candidate);
      if (rows.length) return rows;
    }
    return [];
  });

  if (chargeRowsFromJobs.length) {
    return chargeRowsFromJobs;
  }

  return normalizeList(singlePrintData?.invoice_charge_line);
};

const resolveCustomerDisplay = (singlePrintData, normalizedJobs = []) => {
  const invoiceCustomerId = singlePrintData?.customer_Id ?? singlePrintData?.customer_id;
  const invoiceCustomerName = String(singlePrintData?.customer_Name || '').trim();
  const firstJobWithCustomer = normalizeList(normalizedJobs).find((job) => {
    const jobCustomerId = job?.customer_Id ?? job?.customer_id;
    return !checkEmpty(job?.customer_Name) || !checkEmpty(jobCustomerId);
  });

  const jobCustomerName = String(firstJobWithCustomer?.customer_Name || '').trim();
  const jobCustomerId = firstJobWithCustomer?.customer_Id ?? firstJobWithCustomer?.customer_id;

  return {
    displayName:
      invoiceCustomerName || jobCustomerName || (!checkEmpty(invoiceCustomerId) ? `Customer #${invoiceCustomerId}` : ''),
    customerId: !checkEmpty(invoiceCustomerId) ? invoiceCustomerId : jobCustomerId,
    hasCustomerIdMismatch:
      !checkEmpty(invoiceCustomerId) &&
      !checkEmpty(jobCustomerId) &&
      String(invoiceCustomerId).trim() !== String(jobCustomerId).trim(),
  };
};

const hasPrintableCharge = (item) => {
  if (!item || typeof item !== 'object') return false;

  const hasAmountField = item.charge_value !== undefined && item.charge_value !== null && String(item.charge_value).trim() !== '';
  const hasUnitField =
    (item.charge_Unit_Amount !== undefined && item.charge_Unit_Amount !== null && String(item.charge_Unit_Amount).trim() !== '') ||
    (item.unit_price !== undefined && item.unit_price !== null && String(item.unit_price).trim() !== '');
  const hasQtyField =
    (item.charge_Qty !== undefined && item.charge_Qty !== null && String(item.charge_Qty).trim() !== '') ||
    (item.basis_par !== undefined && item.basis_par !== null && String(item.basis_par).trim() !== '');

  if (!(hasAmountField || hasUnitField || hasQtyField)) return false;
  return Math.abs(getRowAmount(item)) >= 0.0001;
};

const getRowAmount = (item) => {
  const qty = toNum(item?.charge_Qty ?? item?.basis_par);
  if (qty <= 0) return 0;
  const unit = toNum(item?.charge_Unit_Amount ?? item?.unit_price);
  return unit * qty;
};

const getBasisPerDisplayValue = (item) => {
  const rawValue = item?.charge_Qty ?? item?.basis_par;
  if (rawValue === undefined || rawValue === null) return '';

  const trimmedValue = String(rawValue).trim();
  if (!trimmedValue) return '';
  if (['null', 'undefined', 'nan'].includes(trimmedValue.toLowerCase())) return '';

  const numericValue = Number(trimmedValue);
  if (Number.isFinite(numericValue)) {
    const integerValue = Math.trunc(numericValue);
    if (integerValue === 0) return '';
    return String(integerValue);
  }

  return trimmedValue;
};

const hasBasisPerValue = (item) => getBasisPerDisplayValue(item) !== '';

const HeaderFixed = ({ singlePrintData, invoiceDate }) => (
  <View style={styles.header} fixed>
    <View style={styles.headerRow}>
      <Text style={styles.headerTextInvoiceNo}>
        Invoice No: <Text style={styles.labelBold}>{singlePrintData?.invoice_No}</Text>
      </Text>
      <Text style={styles.invoiceTitle}>Invoice</Text>
      <Text style={styles.headerText}>
        Date: <Text style={styles.labelBold}>{invoiceDate}</Text>
      </Text>
    </View>
  </View>
);

const RemarksAndSignatures = ({ singlePrintData }) => (
  <View style={styles.remarksSignatureWrapper}>
    <View style={styles.remarksLine}>
      <Text>Remarks: </Text>
      <Text style={styles.labelBold}>{singlePrintData?.invoice_Remarks || ''}</Text>
    </View>

    <View style={styles.signaturesContainer}>
      <View style={styles.signatureBlock}>
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Receiver Signature/Date</Text>
      </View>

      <View style={styles.signatureBlock}>
        <View style={styles.signatureLine} />
        <Text style={styles.signatureLabel}>Accountant</Text>
      </View>
    </View>
  </View>
);

// ✅ Important: wrap={false} + minPresenceAhead → prevents printing too low on the page
const InvoiceClosingBlock = ({ totalAmount, singlePrintData }) => (
  <View wrap={false} minPresenceAhead={CLOSING_BLOCK_MIN_SPACE}>
    <View style={styles.grandTotalWrapper}>
      <View>
        <Text style={styles.grandTotalTitle}>Grand Total</Text>
        <Text style={styles.amountInWords}>
          {convertToWords(totalAmount)}
        </Text>
      </View>
      <View>
        <Text style={styles.grandTotalText}>
          {`QAR ${number_format(totalAmount, 2)}`}
        </Text>
      </View>
    </View>

    <RemarksAndSignatures singlePrintData={singlePrintData} />
  </View>
);

const InvoicePdfDocument = ({ singlePrintData }) => {
  console.log('singlePrintData-----------------------------', singlePrintData);
  const normalizedJobs = getNormalizedInvoiceJobs(singlePrintData);
  const jobList = getChargeRowsFromInvoice(singlePrintData, normalizedJobs);
  const runtimeAmount = sumChargeRows(jobList);
  const totalAmount = getFirstNumericValue(
    runtimeAmount,
    singlePrintData?.invoice_Summary_TotalAmount,
    singlePrintData?.invoice_Amount,
  );
  const invoiceMode = getInvoiceDisplayMode(singlePrintData, normalizedJobs);
  const customerDisplay = resolveCustomerDisplay(singlePrintData, normalizedJobs);

  const invoiceDate = singlePrintData?.invoice_Date
    ? String(singlePrintData.invoice_Date).split(' ')[0]
    : '';

  const printableJobList = (jobList || []).filter((item) => hasPrintableCharge(item));
  const showBasisPerColumn = printableJobList.some(hasBasisPerValue);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Fixed header */}
        <HeaderFixed singlePrintData={singlePrintData} invoiceDate={invoiceDate} />

        {/* Customer name */}
        <View style={styles.section}>
          <Text>Customer Name:</Text>
          <Text style={styles.customerNameLine}>{customerDisplay.displayName}</Text>
        </View>

        {/* BL / Doc / Mode */}
        <View style={[styles.section, styles.row]}>
          <View style={styles.cell}>
            <Text style={styles.labelBold}>BL NO/ AWBL NO:</Text>
            <Text>{singlePrintData?.invoice_AWBL_Number || ''}</Text>
          </View>

          <View style={styles.cell}>
            <Text style={styles.labelBold}>Document no/ Customs Declaration:</Text>
            <Text>
              {normalizeList(singlePrintData?.doc_number)
                .map((v) => String(v).trim())
                .filter(Boolean)
                .join(', ')}
            </Text>
          </View>

          <View style={styles.cell}>
            <Text style={styles.labelBold}>Mode:</Text>
            <Text>{invoiceMode}</Text>
          </View>
        </View>

        {/* Charges table */}
        <View style={styles.tableWrapper}>
          {/* repeat header on every page */}
          <View style={styles.tableHeaderRow} fixed>
            <Text style={[styles.thFirst, { width: '8%' }, styles.textCenter]}>Sr</Text>
            <Text style={[styles.th, { width: '44%' }]}>Description of Charges</Text>
            <Text style={[styles.th, { width: '16%' }, styles.textCenter]}>Unit Price</Text>
            {showBasisPerColumn && (
              <Text style={[styles.th, { width: '16%' }, styles.textCenter]}>Basis Per</Text>
            )}
            <Text style={[styles.thLast, { width: showBasisPerColumn ? '16%' : '32%' }, styles.textRight]}>
              Amount
            </Text>
          </View>

          {printableJobList.map((item, index) => (
            (() => {
              const basisPerValue = getBasisPerDisplayValue(item);

              return (
                <View style={styles.tdRow} key={`${item?.charge_Name}-${index}`} wrap>
                  <Text style={[styles.td, { width: '8%' }, styles.textCenter]}>{index + 1}</Text>
                  <Text style={[styles.td, { width: '44%' }]}>{item?.charge_Name || ''}</Text>
                  <Text style={[styles.td, { width: '16%' }, styles.textCenter]}>
                    {number_format(item?.charge_Unit_Amount ?? item?.unit_price, 2)}
                  </Text>
                  {showBasisPerColumn && (
                    <Text style={[styles.td, { width: '16%' }, styles.textCenter]}>
                      {basisPerValue}
                    </Text>
                  )}
                  <Text style={[styles.tdLast, { width: showBasisPerColumn ? '16%' : '32%' }, styles.textRight]}>
                    {number_format(getRowAmount(item), 2)}
                  </Text>
                </View>
              );
            })()
          ))}
        </View>

        {/* ✅ Closing block: will never go too low; moves to next page if needed */}
        <InvoiceClosingBlock totalAmount={totalAmount} singlePrintData={singlePrintData} />
      </Page>
    </Document>
  );
};

export const InvoicePdfPage = ({ singlePrintData }) => {
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    let objectUrl;

    const generatePdf = async () => {
      const blob = await pdf(<InvoicePdfDocument singlePrintData={singlePrintData} />).toBlob();
      objectUrl = URL.createObjectURL(blob);
      setPdfUrl(objectUrl + '#zoom=90%');
    };

    generatePdf();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [singlePrintData]);

  if (!pdfUrl) return <div>Generating invoice…</div>;

  return (
    <div style={{ height: '85vh', width: '100%' }}>
      <iframe
        src={pdfUrl}
        title="Invoice Preview"
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

export default InvoicePdfDocument;
